import { useMemo } from "react"

import { useBounceGlobalValues, useDisplayRegionName, useFormatDecimals, useTranslation } from "@bounce/i18n"
import { useStoreTypeText } from "@bounce/store"
import type { Nullable } from "@bounce/util"

import type { NearbyStorageSpot, StoreData } from "../../../../libs/bounce/stores/types"
import { useUrl } from "../../../../libs/router"
import { getCityCurrency } from "../../../../utils/city"
import type { BaseStoreInterpolations } from "../utils/getStoreInterpolations"
import { getBaseStoreInterpolations } from "../utils/getStoreInterpolations"

export type Interpolations = BaseStoreInterpolations & {
  MOBILE_DOWNLOAD_URL: string
}

type Options = {
  store: StoreData | NearbyStorageSpot
  pois: Nullable<{ translatedName: string }[]>
}

export const useInterpolations = ({ store, pois }: Options): Interpolations => {
  const bounceGlobals = useBounceGlobalValues({
    currency: getCityCurrency(store.city),
  })
  const displayRegionName = useDisplayRegionName()
  const formatCapacity = useFormatDecimals({ minDecimalPlaces: 0, maxDecimalPlaces: 0 })
  const { i18n, t } = useTranslation("marketing-ls-store")
  const { getUrl } = useUrl()
  const storeType = useStoreTypeText(store.type)

  const interpolations = useMemo(
    () => ({
      ...getBaseStoreInterpolations({
        store,
        pois,
        language: i18n.language,
        bounceGlobals,
        displayRegionName,
        formatCapacity,
        storeType,
        unknownCapacityText: t("screen.storeScreen.unknownCapacity", "unlimited"),
      }),
      MOBILE_DOWNLOAD_URL: getUrl({ path: "#appDownload" }),
    }),
    [store, pois, i18n.language, bounceGlobals, displayRegionName, formatCapacity, storeType, t, getUrl],
  )

  return interpolations
}
